import React, { useEffect } from 'react';
import { ArrowLeft, Truck, RefreshCw, ShieldCheck } from 'lucide-react';

interface PolicyPageProps {
  onBack: () => void;
  initialSection?: 'shipping' | 'refund';
}

export const PolicyPage: React.FC<PolicyPageProps> = ({ onBack, initialSection }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    if (initialSection) {
      setTimeout(() => {
        const element = document.getElementById(initialSection);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [initialSection]);

  return (
    <div className="bg-earth-50 min-h-screen pt-10 pb-20 animate-fade-in">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <button 
          onClick={onBack}
          className="flex items-center text-herbal-800 font-medium mb-8 hover:underline hover:text-herbal-900 transition-colors group"
        >
          <ArrowLeft className="h-5 w-5 mr-2 transform group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </button>

        <div className="bg-white rounded-3xl shadow-xl border border-earth-100 overflow-hidden p-8 lg:p-16 max-w-4xl mx-auto">
          <h1 className="font-heading text-4xl font-bold text-herbal-900 mb-12 text-center">
            Store Policies
          </h1>

          {/* Shipping Policy */}
          <section id="shipping" className="mb-16">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-turmeric-100 rounded-full flex items-center justify-center text-turmeric-600">
                <Truck className="h-6 w-6" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-herbal-800">Shipping Policy</h2>
            </div>
            
            <div className="prose prose-lg text-gray-600 pl-0 sm:pl-16">
              <p className="mb-4">
                At Age Life, we are committed to delivering our natural herbal remedies to our customers across East Africa efficiently and safely.
              </p>
              
              <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3">Delivery Coverage</h3>
              <p className="mb-4">
                We currently ship to the following countries:
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li><strong>Kenya:</strong> Nationwide delivery available. (Nairobi HQ)</li>
                <li><strong>Uganda:</strong> Deliveries processed via our Kampala network.</li>
                <li><strong>Tanzania:</strong> Shipping available to Dar es Salaam and major regions.</li>
                <li><strong>Rwanda:</strong> Services available in Kigali.</li>
                <li><strong>Somalia:</strong> Delivery services available in Mogadishu.</li>
                <li><strong>Ethiopia:</strong> Shipping available to Addis Ababa.</li>
              </ul>

              <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3">Delivery Times</h3>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li><strong>Nairobi & Environs:</strong> Same day or next day delivery.</li>
                <li><strong>Other Kenya Regions:</strong> 1-2 business days.</li>
                <li><strong>International (East Africa):</strong> 3-7 business days depending on customs and distance.</li>
              </ul>
              
              <p className="text-sm italic mt-4 bg-earth-50 p-4 rounded-lg">
                Note: Shipping costs may vary based on location and package weight. Exact rates will be calculated at checkout or provided by our sales team via WhatsApp.
              </p>
            </div>
          </section>

          {/* Divider */}
          <hr className="border-gray-200 my-12" />

          {/* Refund Policy */}
          <section id="refund">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-turmeric-100 rounded-full flex items-center justify-center text-turmeric-600">
                <RefreshCw className="h-6 w-6" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-herbal-800">Refund & Return Policy</h2>
            </div>
            
            <div className="prose prose-lg text-gray-600 pl-0 sm:pl-16">
              <p className="mb-4">
                Your satisfaction is our priority. If you are not completely satisfied with your purchase, we are here to help.
              </p>

              <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3">Eligibility for Returns</h3>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>Items must be unused and in the same condition that you received them.</li>
                <li>Items must be in the original packaging (seals must not be broken for health and safety reasons).</li>
                <li>Returns must be initiated within <strong>7 days</strong> of delivery.</li>
              </ul>

              <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3">Refund Process</h3>
              <p className="mb-4">
                Once we receive your returned item, we will inspect it and notify you on the status of your refund. 
              </p>
              <p className="mb-4">
                If your return is approved, we will initiate a refund to your original method of payment (e.g., M-Pesa or Bank Transfer).
              </p>

               <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3">Damaged or Wrong Items</h3>
               <p className="mb-4">
                 If you received a damaged product or the wrong item, please contact us immediately via WhatsApp or Phone. We will arrange for a replacement to be sent to you at no extra cost.
               </p>

               <div className="flex items-center gap-2 mt-8 text-herbal-800 font-medium">
                  <ShieldCheck className="h-5 w-5" />
                  <span>Shop with confidence.</span>
               </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};